import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Eraser,
  ArrowDown,
  ArrowUp,
  BatteryCharging,
  BatteryFull,
  Check,
  PackageCheck,
  BatteryLow,
  Lightbulb,
  Loader2,
  Watch,
} from "lucide-react";
import betaAPI from "v2/api";
import { useCaseCommand, useCaseStream } from "hooks/useCaseDashboard";
import useWearableLabels from "v2/hooks/useWearableLabels";
import { formatDuration } from "utils/caseDashboard";
import { showToast } from "utils/toast";
import {
  FLEET_SORTS,
  FLEET_STALE_S,
  fleetSummary,
  fleetVersionLabel,
  groupFleet,
  clearErrorKey,
  clearProgressFrom,
  clearState,
  identifyErrorKey,
  identifyState,
  isLowBattery,
  productVersionsFrom,
  versionState,
} from "utils/caseFleet";
import ClearProgress from "v2/views/components/common/ClearProgress";
import ConfirmDialog from "v2/views/components/common/ConfirmDialog";
import { Card, EmptyState, Notice, StatTile } from "./parts";

const POLL_MS = 10000;
const ROW =
  "grid grid-cols-[minmax(0,12rem)_9rem_9rem_6rem_minmax(0,1fr)_auto] items-center gap-4 px-4";
// The wearable blinks for 5 s; keep "Blinking" up that long.
const BLINK_MS = 5000;

/** The latest production firmware (the target) and product-version labels. */
const useFirmwareTarget = () => {
  const [target, setTarget] = useState({ version: null, products: new Map() });
  useEffect(() => {
    let alive = true;
    Promise.all([
      betaAPI.getWearableFirmwareLatest({ channel: "production" }),
      betaAPI.getWearableFirmwareReleases({ channel: "production" }),
    ])
      .then(([latest, list]) => {
        if (!alive) return;
        setTarget({
          version: latest.data?.base_version || null,
          products: productVersionsFrom(list.data?.releases),
        });
      })
      .catch((e) => console.error("Failed to load firmware releases", e));
    return () => {
      alive = false;
    };
  }, []);
  return target;
};

/**
 * Every wearable the case has heard (`dashboard fleet`): which are in the
 * case, on the latest firmware, and charged. Docked wearables on firmware
 * v0.9.0+ report their version; v0.10.0+ whether they're charging.
 */
const FleetView = ({ caseId }) => {
  const { t } = useTranslation();
  const fleet = useCaseCommand(caseId, "fleet", POLL_MS);
  const { version: target, products } = useFirmwareTarget();
  const labels = useWearableLabels();
  // null = needs attention first; else { key, dir } from a column header.
  const [sort, setSort] = useState(null);
  const sortBy = (key) =>
    setSort((prev) =>
      prev?.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: FLEET_SORTS[key] }
    );

  if (!fleet.data) {
    if (fleet.loading) return null;
    return <Notice tone="warn">{t("caseDashboard.fleet.unsupported")}</Notice>;
  }

  // The number printed on each case, from the API.
  const units = (fleet.data.wearables || []).map((u) => ({
    ...u,
    label: labels.forMac(u.mac),
  }));
  const summary = fleetSummary(units, target);
  const groups = groupFleet(units, target, sort);
  const targetLabel = fleetVersionLabel(target, products);
  const age = fleet.data.age_s;

  return (
    <div className="flex flex-col gap-4">
      {fleet.data.stale ? (
        <Notice tone="warn">
          {t("caseDashboard.fleet.appDown", {
            time: formatDuration(age),
          })}
        </Notice>
      ) : (
        age > FLEET_STALE_S && (
          <Notice tone="warn">
            {t("caseDashboard.fleet.old", { time: formatDuration(age) })}
          </Notice>
        )
      )}

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile
          icon={Watch}
          label={t("caseDashboard.fleet.tiles.inCase")}
          value={summary.inCase}
          hint={t("caseDashboard.fleet.tiles.nearby", {
            count: summary.nearby,
          })}
        />
        <StatTile
          icon={PackageCheck}
          label={t("caseDashboard.fleet.tiles.upToDate")}
          value={
            target
              ? t("caseDashboard.fleet.tiles.ofTotal", {
                  current: summary.upToDate,
                  total: summary.inCase,
                })
              : "—"
          }
          tone={summary.behind > 0 ? "warn" : undefined}
          hint={
            !target
              ? t("caseDashboard.fleet.tiles.noTarget")
              : summary.behind > 0
              ? t("caseDashboard.fleet.tiles.behindVersion", {
                  count: summary.behind,
                  version: targetLabel,
                })
              : t("caseDashboard.fleet.tiles.latestRelease", {
                  version: targetLabel,
                })
          }
        />
        <StatTile
          icon={BatteryLow}
          label={t("caseDashboard.fleet.tiles.lowBattery")}
          value={summary.lowBattery}
          tone={summary.lowBattery > 0 ? "warn" : undefined}
          hint={t("caseDashboard.fleet.tiles.lowBatteryHint", {
            percent: 20,
          })}
        />
        <StatTile
          icon={BatteryCharging}
          label={t("caseDashboard.fleet.tiles.charging")}
          value={summary.charging}
          hint={t("caseDashboard.fleet.tiles.charged", {
            count: summary.charged,
          })}
        />
      </div>

      {units.length === 0 ? (
        <Card>
          <EmptyState
            icon={Watch}
            title={t("caseDashboard.fleet.empty")}
            body={t("caseDashboard.fleet.emptyHint")}
          />
        </Card>
      ) : (
        ["case", "nearby", "away"].map(
          (where) =>
            groups[where].length > 0 && (
              <FleetGroup
                key={where}
                caseId={caseId}
                where={where}
                units={groups[where]}
                target={target}
                products={products}
                sort={sort}
                onSort={sortBy}
                onChanged={fleet.refresh}
              />
            )
        )
      )}
    </div>
  );
};

const FleetGroup = ({
  caseId,
  where,
  units,
  target,
  products,
  sort,
  onSort,
  onChanged,
}) => {
  const { t } = useTranslation();
  const away = where === "away";
  return (
    <Card
      title={t(`caseDashboard.fleet.groups.${where}`)}
      action={
        <span className="text-xs tabular-nums text-[#6B7280]">
          {units.length}
        </span>
      }
    >
      <div
        className={`${ROW} border-b border-[#F3F4F6] py-2 text-xs text-[#6B7280]`}
      >
        <SortHeader
          column="wearable"
          sortKey="name"
          sort={sort}
          onSort={onSort}
        />
        <span>{t("caseDashboard.fleet.columns.firmware")}</span>
        <SortHeader
          column="battery"
          sortKey="battery"
          sort={sort}
          onSort={onSort}
        />
        <SortHeader
          column="records"
          sortKey="records"
          sort={sort}
          onSort={onSort}
        />
        <span>
          {away
            ? t("caseDashboard.fleet.columns.lastSeen")
            : t("caseDashboard.fleet.columns.power")}
        </span>
        <span />
      </div>
      <ul className="divide-y divide-[#F3F4F6]">
        {units.map((u) => (
          <li key={u.mac} className={`${ROW} py-2.5 text-[13px]`}>
            <WearableName unit={u} />
            <FirmwareCell unit={u} target={target} products={products} />
            <BatteryCell unit={u} muted={away} />
            <span
              className={`tabular-nums ${
                u.records > 0 ? "text-[#374151]" : "text-[#9CA3AF]"
              }`}
              title={
                u.records > 0
                  ? t("caseDashboard.fleet.toSync", { count: u.records })
                  : undefined
              }
            >
              {u.records > 0 ? u.records : "—"}
            </span>
            {away ? (
              <span className="text-[#6B7280]">
                {u.last_seen
                  ? t("caseDashboard.fleet.ago", {
                      time: formatDuration(Date.now() / 1000 - u.last_seen),
                    })
                  : "—"}
              </span>
            ) : (
              <PowerCell power={u.power} />
            )}
            <span className="flex items-center justify-end gap-2">
              <ClearCell caseId={caseId} unit={u} onCleared={onChanged} />
              <IdentifyCell caseId={caseId} unit={u} />
            </span>
          </li>
        ))}
      </ul>
    </Card>
  );
};

// The case label (e.g. 002) when the API knows it, then the Bluetooth name.
const WearableName = ({ unit }) => (
  <span className="flex min-w-0 items-baseline gap-2">
    {unit.label && (
      <span className="text-[14px] font-semibold tabular-nums text-[#111827]">
        {unit.label}
      </span>
    )}
    <span
      className={`truncate font-mono ${
        unit.label
          ? "text-[12px] text-[#6B7280]"
          : "text-[13px] font-medium text-[#111827]"
      }`}
    >
      {unit.name}
    </span>
  </span>
);

// A column header that sorts the list; the arrow shows the active order.
const SortHeader = ({ column, sortKey, sort, onSort }) => {
  const { t } = useTranslation();
  const label = t(`caseDashboard.fleet.columns.${column}`);
  const active = sort?.key === sortKey;
  const Arrow = active && sort.dir === "desc" ? ArrowDown : ArrowUp;
  return (
    <button
      type="button"
      onClick={() => onSort(sortKey)}
      aria-label={t("caseDashboard.fleet.sortBy", { column: label })}
      aria-sort={
        active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"
      }
      className={`flex items-center gap-1 text-left transition duration-150 hover:text-[#111827] ${
        active ? "font-medium text-[#111827]" : ""
      }`}
    >
      {label}
      <Arrow
        size={12}
        className={active ? "text-[#E07A3A]" : "text-[#D1D5DB]"}
      />
    </button>
  );
};

const FirmwareCell = ({ unit, target, products }) => {
  const { t } = useTranslation();
  if (!unit.fw) {
    return (
      <span className="text-[#9CA3AF]">
        {t("caseDashboard.fleet.versionUnknown")}
      </span>
    );
  }
  const state = versionState(unit.fw, target);
  return (
    <span
      className={`flex min-w-0 items-center gap-1 ${
        state === "current"
          ? "text-[#047857]"
          : state === "behind"
          ? "text-[#B45309]"
          : "text-[#374151]"
      }`}
      title={
        state === "behind" ? t("caseDashboard.fleet.behindTitle") : undefined
      }
    >
      {state === "current" && <Check size={13} className="shrink-0" />}
      <span className="truncate font-medium">
        {fleetVersionLabel(unit.fw, products)}
      </span>
      {unit.build && unit.build !== "production" && (
        <span className="shrink-0 text-xs text-[#9CA3AF]">
          {t(`caseDashboard.fleet.builds.${unit.build}`)}
        </span>
      )}
    </span>
  );
};

const BatteryCell = ({ unit, muted }) => {
  const { t } = useTranslation();
  if (unit.battery == null) return <span className="text-[#9CA3AF]">—</span>;
  const low = isLowBattery(unit);
  const color = muted ? "#D1D5DB" : low ? "#DC2626" : "#10B981";
  return (
    <span className="flex items-center gap-2">
      <span className="h-1.5 w-14 overflow-hidden rounded-full bg-[#F3F4F6]">
        <span
          className="block h-full rounded-full"
          style={{
            width: `${Math.max(4, unit.battery)}%`,
            backgroundColor: color,
          }}
        />
      </span>
      <span
        className={`tabular-nums ${
          low && !muted ? "font-medium text-[#B91C1C]" : "text-[#374151]"
        }`}
      >
        {t("caseDashboard.fleet.percent", { value: unit.battery })}
      </span>
    </span>
  );
};

/** Make a docked wearable blink white for 5 s so it can be found. */
// Erase every record on a docked wearable (firmware v0.14.0+), through the
// case (`dashboard clear <MAC>`), after a confirmation: it can't be undone.
const ClearCell = ({ caseId, unit, onCleared }) => {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const [clearing, setClearing] = useState(false);
  // Streamed, so the case's @@PROGRESS lines arrive as it erases.
  const stream = useCaseStream(caseId, { maxLines: 50 });
  const alive = useRef(true);
  useEffect(() => () => (alive.current = false), []);

  const state = clearState(unit);
  if (!state) return null;
  const name = unit.label ? `${unit.label} (${unit.name})` : unit.name;

  const clear = async () => {
    setConfirming(false);
    setClearing(true);
    let res = null;
    try {
      res = await stream.start(`clear ${unit.mac}`);
    } catch (e) {
      console.error("Clear failed", e);
    }
    if (!alive.current) return;
    setClearing(false);
    if (res?.json?.ok) {
      showToast.success(t("caseDashboard.fleet.clear.done", { name }));
      onCleared?.();
      return;
    }
    const key = res?.json ? clearErrorKey(res.json.error) : "unsupported";
    showToast.error(
      t(`caseDashboard.fleet.clear.errors.${key}`, {
        name,
        error: res?.json?.error,
      })
    );
  };

  const old = state === "old-firmware";
  return (
    <>
      <button
        onClick={() => setConfirming(true)}
        disabled={old || clearing}
        title={
          old
            ? t("caseDashboard.fleet.clear.oldFirmwareTitle")
            : t("caseDashboard.fleet.clear.title")
        }
        className={`relative flex items-center gap-1.5 overflow-hidden rounded-lg border px-2.5 py-1 text-xs font-medium transition duration-150 disabled:cursor-default ${
          clearing
            ? "border-[#FECACA] bg-white text-[#B91C1C]"
            : "border-[#E5E7EB] bg-white text-[#374151] hover:border-[#FECACA] hover:bg-[#FEF2F2] hover:text-[#DC2626] disabled:opacity-50 disabled:hover:border-[#E5E7EB] disabled:hover:bg-white disabled:hover:text-[#374151]"
        }`}
      >
        {clearing ? (
          <ClearProgress progress={clearProgressFrom(stream.lines)} />
        ) : (
          <>
            <Eraser size={13} />
            {t("caseDashboard.fleet.clear.button")}
          </>
        )}
      </button>
      <ConfirmDialog
        open={confirming}
        danger
        title={t("caseDashboard.fleet.clear.confirmTitle", { name })}
        body={t("caseDashboard.fleet.clear.confirmBody")}
        details={
          unit.records > 0 ? (
            <p className="text-[13px] font-medium text-[#B91C1C]">
              {t("caseDashboard.fleet.clear.confirmRecords", {
                count: unit.records,
              })}
            </p>
          ) : null
        }
        confirmLabel={t("caseDashboard.fleet.clear.confirm")}
        onConfirm={clear}
        onCancel={() => setConfirming(false)}
      />
    </>
  );
};

const IdentifyCell = ({ caseId, unit }) => {
  const { t } = useTranslation();
  const [phase, setPhase] = useState(null); // asking | blinking
  const alive = useRef(true);
  useEffect(() => () => (alive.current = false), []);

  const state = identifyState(unit);
  if (!state) return <span />;

  const identify = async () => {
    setPhase("asking");
    let res = null;
    try {
      res = await window.electronAPI.caseDashboard.run(
        caseId,
        `identify ${unit.mac}`
      );
    } catch (e) {
      console.error("Identify failed", e);
    }
    if (!alive.current) return;
    if (res?.json?.ok) {
      setPhase("blinking");
      setTimeout(() => alive.current && setPhase(null), BLINK_MS);
      return;
    }
    setPhase(null);
    const key = res?.json ? identifyErrorKey(res.json.error) : "unsupported";
    showToast.error(
      t(`caseDashboard.fleet.identify.errors.${key}`, {
        name: unit.name,
        error: res?.json?.error,
      })
    );
  };

  const old = state === "old-firmware";
  return (
    <button
      onClick={identify}
      disabled={old || !!phase}
      title={
        old
          ? t("caseDashboard.fleet.identify.oldFirmwareTitle")
          : t("caseDashboard.fleet.identify.title")
      }
      className="flex items-center gap-1.5 rounded-lg border border-[#E5E7EB] bg-white px-2.5 py-1 text-xs font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB] disabled:cursor-default disabled:opacity-50"
    >
      {phase === "asking" ? (
        <Loader2 size={13} className="animate-spin" />
      ) : (
        <Lightbulb
          size={13}
          className={phase === "blinking" ? "text-[#E07A3A]" : undefined}
        />
      )}
      {phase === "blinking"
        ? t("caseDashboard.fleet.identify.blinking")
        : t("caseDashboard.fleet.identify.button")}
    </button>
  );
};

const POWER_ICON = { charging: BatteryCharging, charged: BatteryFull };

const PowerCell = ({ power }) => {
  const { t } = useTranslation();
  if (!power) return <span className="text-[#9CA3AF]">—</span>;
  const Icon = POWER_ICON[power];
  return (
    <span
      className={`flex items-center gap-1.5 ${
        power === "charging"
          ? "text-[#B45309]"
          : power === "charged"
          ? "text-[#047857]"
          : "text-[#6B7280]"
      }`}
    >
      {Icon && <Icon size={14} className="shrink-0" />}
      {t(`caseDashboard.fleet.power.${power}`)}
    </span>
  );
};

export default FleetView;
